import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, Minus, AlertTriangle } from 'lucide-react';
import { useRunSegments } from '@/hooks/useRunSegments';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';

interface TrailSegmentFeedbackProps {
  runId: string;
}

interface SegmentFeedback {
  segmentId: string;
  sentiment: number | null;
  comment: string;
}

const TrailSegmentFeedback = ({ runId }: TrailSegmentFeedbackProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { segments, isLoading } = useRunSegments(runId);
  const { toast } = useToast();
  const [feedback, setFeedback] = useState<Record<string, SegmentFeedback>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Initialize feedback from existing segment data
  useEffect(() => {
    if (segments.length > 0) {
      const initialFeedback: Record<string, SegmentFeedback> = {};
      segments.forEach(segment => {
        initialFeedback[segment.id] = {
          segmentId: segment.id,
          sentiment: segment.difficulty_sentiment ?? null,
          comment: segment.difficulty_comment || '',
        };
      });
      setFeedback(initialFeedback);
    }
  }, [segments]);

  const handleSentimentChange = async (segmentId: string, sentiment: string) => {
    const sentimentValue = sentiment === 'null' ? null : parseInt(sentiment);
    
    setFeedback(prev => ({
      ...prev,
      [segmentId]: {
        ...prev[segmentId],
        sentiment: sentimentValue,
      }
    }));

    // Auto-save
    await saveFeedback(segmentId, sentimentValue, feedback[segmentId]?.comment || '');
  };

  const handleCommentChange = (segmentId: string, comment: string) => {
    if (comment.length <= 150) {
      setFeedback(prev => ({
        ...prev,
        [segmentId]: {
          ...prev[segmentId],
          comment,
        }
      }));
    }
  };

  const handleCommentBlur = async (segmentId: string) => {
    // Save comment when user leaves the field
    await saveFeedback(
      segmentId,
      feedback[segmentId]?.sentiment ?? null,
      feedback[segmentId]?.comment || ''
    );
  };

  const saveFeedback = async (segmentId: string, sentiment: number | null, comment: string) => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('run_segments')
        .update({
          difficulty_sentiment: sentiment,
          difficulty_comment: comment || null,
        })
        .eq('id', segmentId);

      if (error) throw error;
    } catch (error) {
      console.error('Error saving feedback:', error);
      toast({
        title: 'Save failed',
        description: 'Could not save your feedback. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getDifficultyLabel = (difficulty: string | undefined) => {
    if (!difficulty) return 'Unknown';
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  };

  const getSentimentIcon = (sentiment: number | null) => {
    switch (sentiment) {
      case -1:
        return <ThumbsUp className="h-4 w-4 text-green-500" />;
      case 0:
        return <Minus className="h-4 w-4 text-blue-500" />;
      case 1:
        return <ThumbsDown className="h-4 w-4 text-orange-500" />;
      case 2:
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  if (isLoading || segments.length === 0) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="cursor-pointer hover:bg-accent/5 transition-colors">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <CardTitle className="flex items-center gap-2">
                  Trail Difficulty Feedback
                  <Badge variant="secondary" className="text-xs">
                    Optional
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Rate how the difficulty matched your experience ({segments.length} segment{segments.length !== 1 ? 's' : ''})
                </CardDescription>
              </div>
              {isOpen ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-6 pt-0">
            {segments.map((segment, index) => {
              const segmentFeedback = feedback[segment.id] || { segmentId: segment.id, sentiment: null, comment: '' };
              const currentSentiment = segmentFeedback.sentiment?.toString() || 'null';
              const currentComment = segmentFeedback.comment || '';

              return (
                <div
                  key={segment.id}
                  className="border border-border rounded-lg p-4 space-y-4 bg-card"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-muted-foreground">
                          Segment {index + 1}
                        </span>
                        {segmentFeedback && segmentFeedback.sentiment !== null && getSentimentIcon(segmentFeedback.sentiment)}
                      </div>
                      <h4 className="font-semibold text-foreground">
                        {segment.trail_segment?.trail_name || 'Unnamed Trail'}
                      </h4>
                      {segment.trail_segment?.segment_description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {segment.trail_segment.segment_description}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="flex-shrink-0">
                      {getDifficultyLabel(segment.trail_segment?.difficulty)}
                    </Badge>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium">
                      How did the difficulty compare to the rating?
                    </Label>
                    <RadioGroup
                      value={currentSentiment}
                      onValueChange={(value) => handleSentimentChange(segment.id, value)}
                      className="grid grid-cols-2 gap-3"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="-1" id={`easier-${segment.id}`} />
                        <Label
                          htmlFor={`easier-${segment.id}`}
                          className="cursor-pointer flex items-center gap-1.5 text-sm"
                        >
                          <ThumbsUp className="h-3.5 w-3.5 text-green-500" />
                          Easier
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="0" id={`asrated-${segment.id}`} />
                        <Label
                          htmlFor={`asrated-${segment.id}`}
                          className="cursor-pointer flex items-center gap-1.5 text-sm"
                        >
                          <Minus className="h-3.5 w-3.5 text-blue-500" />
                          As Rated
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="1" id={`harder-${segment.id}`} />
                        <Label
                          htmlFor={`harder-${segment.id}`}
                          className="cursor-pointer flex items-center gap-1.5 text-sm"
                        >
                          <ThumbsDown className="h-3.5 w-3.5 text-orange-500" />
                          Harder
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="2" id={`muchharder-${segment.id}`} />
                        <Label
                          htmlFor={`muchharder-${segment.id}`}
                          className="cursor-pointer flex items-center gap-1.5 text-sm"
                        >
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                          Much Harder
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`comment-${segment.id}`} className="text-sm">
                      Additional notes (optional)
                    </Label>
                    <Textarea
                      id={`comment-${segment.id}`}
                      placeholder="What made it feel different? Weather, conditions, vehicle type..."
                      value={currentComment}
                      onChange={(e) => handleCommentChange(segment.id, e.target.value)}
                      onBlur={() => handleCommentBlur(segment.id)}
                      rows={2}
                      className="text-sm resize-none"
                    />
                    <p className="text-xs text-muted-foreground text-right">
                      {currentComment.length}/150 characters
                    </p>
                  </div>
                </div>
              );
            })}

            {isSaving && (
              <p className="text-xs text-muted-foreground text-center">
                Saving feedback...
              </p>
            )}

            <p className="text-sm text-muted-foreground">
              💡 Your feedback helps others prepare for similar conditions and equipment types.
            </p>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default TrailSegmentFeedback;
